import {useCallback, useState} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {useNavigate} from 'react-router-dom'
import {AxiosError} from 'axios'
import {useErrorStore} from '@src/Layers/ErrorLayer/ErrorStore.ts'
import {getApiErrorMessage, isConflictError} from '@src/httpClient/utils/apiError.ts'

/**
 * Runs an approve/reject/cancel call on a workflow request.
 * Guards against double clicks (the backend has no row locking for these) and
 * treats HTTP 409 as "already processed elsewhere": refetch and leave the page.
 */
export const useRequestAction = (entityKey: string, listLink: string) => {
    const [actionInFlight, setActionInFlight] = useState(false)
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const setAppError = useErrorStore(x => x.setAppError)

    const runAction = useCallback((action: () => Promise<unknown>) => {
        if (actionInFlight) return
        setActionInFlight(true)

        const finish = () => {
            queryClient.invalidateQueries({queryKey: [entityKey]})
            queryClient.invalidateQueries({queryKey: ["requests-list"]})
            navigate(listLink)
        }

        action()
            .then(finish)
            .catch((error: AxiosError) => {
                if (isConflictError(error)) {
                    finish()
                    return
                }
                setAppError({
                    axiosError: error,
                    message: getApiErrorMessage(error),
                    showModal: true,
                    retry: async () => action().then(finish),
                })
            })
            .finally(() => setActionInFlight(false))
    }, [actionInFlight, entityKey, listLink, queryClient, navigate, setAppError])

    return {actionInFlight, runAction}
}
